import { Container, Graphics } from 'pixi.js';

/**
 * Rim-light chase animation — N small bulbs equally spaced around
 * the wheel's outer perimeter, lighting up in sequence so the rim
 * reads as "alive" during STAKE and pulsing faster post-spin once
 * the wheel lands. Producer recipe lifted directly from the legacy
 * React-CSS keyframe (the lights pulse with a phase offset per bulb
 * driven by an internal time accumulator).
 */

export interface WheelRimLightsOptions {
  /** Diameter of the imaginary circle the lights sit on. Each
   *  bulb is positioned at this radius from the centre, regardless
   *  of where the host adds this container in stage space. */
  diameter: number;
  /** Number of lights (default 16). */
  count?: number;
  /** Animation cycle length in milliseconds. Shorter = faster
   *  chase. The host scene calls {@link setCycleMs} to bump from
   *  the slow STAKE rhythm to the snappier post-landing pulse. */
  cycleMs?: number;
  /** Bulb diameter in pixels. */
  bulbSize?: number;
}

export class WheelRimLights extends Container {
  private readonly bulbs: Graphics[] = [];
  private cycleMs: number;
  private readonly count: number;
  private elapsedMs = 0;
  private diameter: number;
  private readonly bulbSize: number;

  constructor(options: WheelRimLightsOptions) {
    super();
    this.count = options.count ?? 16;
    this.cycleMs = options.cycleMs ?? 1600;
    this.bulbSize = options.bulbSize ?? 8;
    this.diameter = options.diameter;
    for (let i = 0; i < this.count; i++) {
      const bulb = new Graphics();
      bulb.alpha = 0.25;
      bulb.scale.set(0.7);
      this.addChild(bulb);
      this.bulbs.push(bulb);
    }
    this.refreshLayout();
  }

  /** Producer brief 2026-05-16 — reposition bulbs for the given
   *  ring diameter so each bulb sits at the actual perimeter
   *  rather than scaled out by container scaling. */
  setDiameter(diameter: number): void {
    if (diameter === this.diameter) return;
    this.diameter = diameter;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    const radius = this.diameter / 2;
    for (let i = 0; i < this.bulbs.length; i++) {
      const bulb = this.bulbs[i]!;
      const angleRad = ((i / this.count) * 360 - 90) * (Math.PI / 180);
      bulb.x = Math.cos(angleRad) * radius;
      bulb.y = Math.sin(angleRad) * radius;
      bulb.clear();
      bulb
        .circle(0, 0, this.bulbSize / 2)
        .fill({ color: 0xffd66b, alpha: 1 })
        .circle(0, 0, this.bulbSize / 2 - 1)
        .fill({ color: 0xfffce0, alpha: 1 });
    }
  }

  /** Update animation cycle length (e.g. faster after wheel lands). */
  setCycleMs(ms: number): void {
    this.cycleMs = ms;
  }

  /** Drive the chase animation. Host scene's ticker calls this on
   *  every frame with the delta in ms. */
  tick(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    const phase = this.elapsedMs / this.cycleMs;
    for (let i = 0; i < this.bulbs.length; i++) {
      const bulb = this.bulbs[i]!;
      // Each bulb's phase is offset by its index — the leading edge
      // of the pulse walks around the rim. Phase wraps via mod 1.
      const localPhase = (phase - i / this.bulbs.length) % 1;
      const wrapped = localPhase < 0 ? localPhase + 1 : localPhase;
      // Peak around localPhase ≈ 0.15 (matches the legacy keyframe).
      if (wrapped < 0.3) {
        const t = wrapped / 0.3;
        // Easing curve: ramp up then ease down via 1 - (t - 0.5)^2 * 4
        const pulse = 1 - Math.abs(t - 0.5) * 2;
        bulb.alpha = 0.25 + pulse * 0.75;
        bulb.scale.set(0.7 + pulse * 0.35);
      } else {
        bulb.alpha = 0.25;
        bulb.scale.set(0.7);
      }
    }
  }
}
